/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */

import { useSelector } from 'react-redux';
import { getBookingDuration as getOrderBookingDuration } from '../utils/orderUtils';
import { academyLogger } from "utils/logger";

const useTimezone = () => {
    const companyDetails = useSelector((state) => state?.companyReducer?.company);
    const zoneId = companyDetails?.operatingLocation?.zoneId || 'America/New_York';
    const countryCode = companyDetails?.operatingLocation?.code || 'US';

    const getTimezoneAbbreviation = () => {
        if (zoneId?.includes('America/') || zoneId?.includes('New_York')) {
            return 'EST';
        } else if (zoneId?.includes('Asia/Kolkata') || zoneId?.includes('Calcutta')) {
            return 'IST';
        } else if (countryCode === 'US') {
            return 'EST';
        } else if (countryCode === 'IN') {
            return 'IST';
        }
        return 'EST';
    };

    const getCurrentTimeInCompanyTimezone = () => {
        if (!zoneId) return new Date();
        try {
            const options = {
                timeZone: zoneId,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            };
            const formatter = new Intl.DateTimeFormat('en-CA', options);
            const parts = formatter.formatToParts(new Date());
            const dateParts = {};
            parts.forEach(part => { dateParts[part.type] = part.value; });
            const dateStr = `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
            const timeStr = `${dateParts.hour}:${dateParts.minute}:${dateParts.second}`;
            return new Date(`${dateStr}T${timeStr}`);
        } catch (error) {
            return new Date();
        }
    };

    /**
     * Format booking duration exactly like ViewInvoice does
     */
    const formatBookingDuration = (order) => {
        try {
            const bookingDuration = getOrderBookingDuration(
                order,
                zoneId,
                (dateString) => {
                    if (!dateString) return '';
                    return new Date(dateString).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    });
                },
                (dateString) => {
                    if (!dateString) return '';
                    return new Date(dateString).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    });
                }
            );

            if (!bookingDuration) return null;

            return {
                ...bookingDuration,
                fullString: bookingDuration.hasMeaningfulTime
                    ? `${bookingDuration.dateStr}, ${bookingDuration.startTime} - ${bookingDuration.endTime} ${getTimezoneAbbreviation()}`
                    : bookingDuration.dateStr
            };
        } catch (error) {
            academyLogger.error('Error formatting booking duration:', error);
            return null;
        }
    };

    /**
     * Simple formatter that returns just the time range
     */
    const formatBookingTimeRange = (order) => {
        const duration = formatBookingDuration(order);
        if (!duration) return 'N/A';
        return `${duration.startTime} - ${duration.endTime} ${getTimezoneAbbreviation()}`;
    };

    /**
     * Simple formatter that returns just the date
     */
    const formatBookingDate = (order) => {
        const duration = formatBookingDuration(order);
        if (!duration) return 'N/A';
        return duration.dateStr;
    };

    /**
     * Full details formatter
     */
    const formatBookingDetails = (order) => {
        const duration = formatBookingDuration(order);
        if (!duration) return 'N/A';
        return duration.fullString;
    };

    /**
     * FORCE company timezone for any date string
     */
    const formatDateInCompanyTimezone = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return 'N/A';
        }
    };

    /**
     * FORCE company timezone for time display
     */
    const formatTimeInCompanyTimezone = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const time = new Date(dateString).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
            return `${time} ${getTimezoneAbbreviation()}`;
        } catch {
            return 'N/A';
        }
    };

    return {
        zoneId,
        countryCode,
        getTimezoneAbbreviation,
        getCurrentTimeInCompanyTimezone,   
        formatDateInCompanyTimezone,
        formatTimeInCompanyTimezone,
        formatBookingDuration,
        formatBookingDate,
        formatBookingTimeRange,
        formatBookingDetails
    };
};

export default useTimezone;
